/**
 * Control words for a hands-free intake (CHANGE_voice_level3 §4). Matched in the browser on the final transcript,
 * before anything is sent: the model never receives "stop". A control word counts when it is the whole utterance,
 * or opens a short one ("go back please", "repeat that"); a longer sentence that happens to start with "stop" or
 * "continue" is an answer.
 */
export type Control =
  | { kind: "repeat" } | { kind: "skip" } | { kind: "go_back" } | { kind: "pause" } | { kind: "resume" } | { kind: "stop" }
  | { kind: "confirm" } | { kind: "read_all"; label: string };

const WORDS: Array<[Control["kind"], string[]]> = [
  ["repeat", ["say that again", "say again", "repeat that", "come again", "repeat", "pardon"]],
  ["skip", ["next question", "skip that", "skip it", "skip", "pass"]],
  ["go_back", ["previous question", "go back", "previous", "back"]],
  ["pause", ["hold on", "hang on", "pause", "wait"]],
  ["resume", ["carry on", "resume", "continue", "go on"]],
  ["stop", ["that's all for now", "finish later", "stop here", "stop"]],
];
const CONFIRM = ["sounds right", "that's right", "thats right", "looks right", "that is right", "all good", "correct", "yes", "yeah", "yep"];
/** An utterance longer than this is an answer even when it opens with a control word. */
const SHORT = 4;

export function normalise(text: string): string {
  return text.toLowerCase().replace(/[’']/g, "'").replace(/[^\p{L}\p{N}' ]+/gu, " ").replace(/\s+/g, " ").trim();
}

function opens(said: string, phrase: string): boolean {
  return said === phrase || (said.startsWith(phrase + " ") && said.split(" ").length <= SHORT);
}

/**
 * The control word in an utterance, or null for an answer. `review` enables the confirm words and "read all
 * {label}"; `paused` limits what counts to resume and stop (anything else said while paused is not for us).
 */
export function matchControl(text: string, ctx: { review?: boolean; labels?: string[]; paused?: boolean } = {}): Control | null {
  const said = normalise(text);
  if (!said) return null;
  if (ctx.review && !ctx.paused) {
    const read = /^(?:read all|read out|read me)\s+(?:of\s+|the\s+)*(.+)$/.exec(said);
    if (read) {
      const wanted = read[1] ?? "";
      const label = (ctx.labels ?? []).find((l) => normalise(l) === wanted) ?? (ctx.labels ?? []).find((l) => normalise(l).includes(wanted) || wanted.includes(normalise(l)));
      if (label) return { kind: "read_all", label };
    }
    if (CONFIRM.some((w) => opens(said, w))) return { kind: "confirm" };
  }
  for (const [kind, phrases] of WORDS) {
    if (ctx.paused && kind !== "resume" && kind !== "stop") continue;
    if (phrases.some((p) => opens(said, p))) return { kind } as Control;
  }
  return null;
}
